import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';

class DashboardService {
  // Get complete dashboard data
  async getDashboardData() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.DATA);
      
      if (response.success) {
        return response.data;
      }
      
      throw new Error(response.message || 'Failed to fetch dashboard data');
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch dashboard data');
    }
  }

  // Get dashboard metrics only
  async getMetrics(days = 30) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.METRICS, {
        params: { days },
      });
      
      if (response.success) {
        return response.data.metrics;
      }
      
      throw new Error(response.message || 'Failed to fetch metrics');
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch metrics');
    }
  }

  // Get chart data
  async getChartData(days = 30) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.CHART_DATA, {
        params: { days },
      });
      
      if (response.success) {
        return response.data.chartData;
      }
      
      throw new Error(response.message || 'Failed to fetch chart data');
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch chart data');
    }
  }

  // Get recent activities
  async getRecentActivities(limit = 20) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.ACTIVITIES, {
        params: { limit },
      });
      
      if (response.success) {
        return response.data.activities;
      }
      
      throw new Error(response.message || 'Failed to fetch activities');
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch activities');
    }
  }

  // Get analytics summary
  async getAnalyticsSummary(period = '30d') {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.ANALYTICS, {
        params: { period },
      });
      
      if (response.success) {
        return response.data.summary;
      }
      
      throw new Error(response.message || 'Failed to fetch analytics');
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch analytics');
    }
  }

  // Get statistics by date range
  async getStatsByDateRange(startDate, endDate) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.STATS, {
        params: { startDate, endDate },
      });
      
      if (response.success) {
        return response.data;
      }
      
      throw new Error(response.message || 'Failed to fetch statistics');
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch statistics');
    }
  }

  // Refresh dashboard data
  async refreshDashboard() {
    try {
      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.REFRESH);
      
      if (response.success) {
        return true;
      }
      
      throw new Error(response.message || 'Failed to refresh dashboard');
    } catch (error) {
      throw new Error(error.message || 'Failed to refresh dashboard');
    }
  }

  // Transform API data to match existing frontend format
  transformDashboardData(apiData) {
    const transformed = {
      metrics: {
        totalInteractions: apiData.metrics.totalInteractions || 0,
        totalInteractionsChange: apiData.metrics.totalInteractionsChange || 0,
        activeChats: apiData.metrics.activeChats || 0,
        activeChatChange: apiData.metrics.activeChatChange || 0,
        responseTime: apiData.metrics.responseTime || 0,
        responseTimeChange: apiData.metrics.responseTimeChange || 0,
        satisfactionRate: apiData.metrics.satisfactionRate || 0,
        satisfactionRateChange: apiData.metrics.satisfactionRateChange || 0,
      },
      chartData: apiData.chartData || [],
      recentActivities: apiData.recentActivities || [],
      widgets: apiData.widgets || [],
      summary: apiData.summary || {
        totalWidgets: 0,
        activeWidgets: 0,
        totalInteractionsToday: 0,
        pendingInteractions: 0,
        topPerformingWidget: null,
      },
    };

    return transformed;
  }

  // Cache management
  getCachedData(key) {
    try {
      const cached = sessionStorage.getItem(`dashboard_${key}`);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        const cacheAge = now - timestamp;
        const maxAge = 5 * 60 * 1000; // 5 minutes

        if (cacheAge < maxAge) {
          return data;
        } else {
          sessionStorage.removeItem(`dashboard_${key}`);
        }
      }
    } catch (error) {
      // Ignore cache errors
    }
    return null;
  }

  setCachedData(key, data) {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(`dashboard_${key}`, JSON.stringify(cacheEntry));
    } catch (error) {
      // Ignore cache errors
    }
  }

  clearCache() {
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('dashboard_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      // Ignore cache errors
    }
  }

  // Get dashboard data with caching
  async getDashboardDataCached() {
    const cachedData = this.getCachedData('data');
    if (cachedData) {
      return cachedData;
    }

    const data = await this.getDashboardData();
    this.setCachedData('data', data);
    return data;
  }

  // Export data functionality
  async exportDashboardData(format = 'json', dateRange = null) {
    try {
      let data;
      
      if (dateRange) {
        data = await this.getStatsByDateRange(dateRange.startDate, dateRange.endDate);
      } else {
        data = await this.getDashboardData();
      }

      if (format === 'csv') {
        return this.convertToCSV(data);
      } else {
        return JSON.stringify(data, null, 2);
      }
    } catch (error) {
      throw new Error(error.message || 'Failed to export data');
    }
  }

  // Convert data to CSV format
  convertToCSV(data) {
    if (!data || !data.chartData) return '';

    const headers = ['Date', 'Interactions', 'Resolved'];
    const rows = data.chartData.map(item => [
      item.date || item.name,
      item.interactions || 0,
      item.resolved || 0,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    return csv;
  }

  // Download exported data
  downloadData(data, filename, format = 'json') {
    const blob = new Blob([data], {
      type: format === 'csv' ? 'text/csv' : 'application/json',
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Create singleton instance
const dashboardService = new DashboardService();

export default dashboardService;